import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setState("sending");
    try {
      await subscribeNewsletter(email);
      setState("success");
      toast.success("You're on the list — welcome to The Green Literacy Letter.");
    } catch (err) {
      setState("idle");
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <section className="bg-goldsoft border-y border-line relative grain" data-testid="newsletter-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 lg:py-24 relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-terra font-semibold mb-4">
            The Green Literacy Letter
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ink leading-[1.1] mb-4 text-balance">
            Seasonal notes on literacy, nature, & wonder
          </h2>
          <p className="text-txtmut leading-relaxed max-w-md">
            Curriculum ideas, children's book recommendations, and forest bathing invitations from Chicago and the Indiana Dunes — a few times a year, never more.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          {state === "success" ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center gap-3 bg-paper border border-line rounded-lg px-6 py-5"
              data-testid="newsletter-success"
            >
              <CheckCircle2 className="w-5 h-5 text-terra shrink-0" />
              <p className="text-ink text-sm">
                Thank you for subscribing — a welcome note from Jen is on its way to <strong>{email}</strong>.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
              <label htmlFor="newsletter-email" className="sr-only">Email address</label>
              <input
                id="newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.org"
                className="flex-1 bg-paper border border-line rounded-full px-6 py-3.5 text-ink placeholder:text-txtmut/50 focus:outline-none focus:ring-2 focus:ring-terra/60 focus:border-transparent transition-shadow"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                disabled={state === "sending"}
                className="inline-flex items-center justify-center gap-2 bg-ink hover:bg-pine text-paper rounded-full px-7 py-3.5 font-medium transition-colors duration-300 disabled:opacity-60"
                data-testid="newsletter-submit-button"
              >
                {state === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Subscribe
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
